import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { ApiResult } from 'src/Libs/api'
import { CompanyErrorNotificationDto } from 'src/Libs/company-settings/dto/company-error-notification.dto'
import { errorNotificationToToModel } from 'src/Libs/company-settings/transformer'
import { CompanyErrorNotificationModel } from 'src/Libs/company-settings/models'

/**
 * Removes the error notification flag for the user of a company
 *
 *
 * @param companyId
 * @param notificationId The id of the original record given by the `users/companies/{cid}/error-notification` route
 * @param authFetch
 */
export const deleteCompanyErrorNotification = async (
  companyId: number | undefined,
  notificationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  if (companyId === undefined) {
    throw new Error('Company ID is required to remove the error notification flag for the user of a company')
  }

  return authFetch({
    method: 'DELETE',
    path: `users/companies/${companyId}/error-notification/${notificationId}`,
  })
    .then(response => {
      if (response.status !== StatusCodes.NO_CONTENT) {
        return new ApiResult(null, true)
      }
      return new ApiResult(null, false)
    })
    .catch(() => {
      return new ApiResult(null, true)
    })
}

/**
 * Adds the error notification flag for the user of a company
 * @param companyId
 * @param userId
 * @param authFetch
 */
export const addCompanyErrorNotification = async (
  companyId: number | undefined,
  userId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number>> => {
  if (companyId === undefined) {
    throw new Error('Company ID is required to add error notification flag for the user of a company')
  }

  const payload = { userId }
  return authFetch({
    method: 'POST',
    payload: payload,
    path: `users/companies/${companyId}/error-notification`,
  })
    .then(response => {
      if (response.status !== StatusCodes.CREATED) {
        return new ApiResult<number>(null, true)
      }
      return new ApiResult<number>(response.data as number, false)
    })
    .catch(() => {
      return new ApiResult<number>(null, true)
    })
}

export const loadAllUsers = async (
  companyId: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<CompanyErrorNotificationModel[]>> => {
  if (companyId === undefined) {
    throw new Error('Company ID is required to load error notifications of all users for the company')
  }

  return authFetch({
    method: 'GET',
    path: `users/companies/${companyId}/error-notification`,
  })
    .then(response => {
      if (response.status === StatusCodes.OK) {
        return new ApiResult<CompanyErrorNotificationModel[]>(errorNotificationToToModel(response.data as CompanyErrorNotificationDto), true)
      }
      return new ApiResult<CompanyErrorNotificationModel[]>(null, false)
    })
    .catch(error => {
      return new ApiResult<CompanyErrorNotificationModel[]>(null, true)
    })
}
